/**
 * Course Model - Centralized schema definition for CE courses
 * This is the single source of truth for course structure
 */

/**
 * Course schema definition
 * All course objects should conform to this structure
 */
export const CourseSchema = {
  // Required fields
  id: {
    type: String,
    required: true,
    description: 'Unique identifier for the course'
  },
  name: {
    type: String,
    required: true,
    description: 'Name of the course'
  },
  competencies: {
    type: Array,
    required: false,
    description: 'Competencies (derived from performance indicators, read-only)'
  },
  providerType: {
    type: String,
    required: true,
    description: 'Type of provider offering the course',
    enum: ['School', 'Private Business', 'Other']
  },
  providerName: {
    type: String,
    required: true,
    description: 'Name of the school or business providing the course'
  },
  hours: {
    type: Number,
    required: true,
    description: 'Number of hours the course takes',
    min: 0
  },
  credits: {
    type: Number,
    required: true,
    description: 'Number of CE credits the course provides (up to 2 decimal places, e.g. 0.25, 0.5, 2.75)',
    min: 0
  },
  costType: {
    type: String,
    required: true,
    description: 'Whether the course is free or paid',
    enum: ['free', 'paid']
  },
  courseType: {
    type: String,
    required: true,
    description: 'Activity/course type (CE activity type)',
    enum: [
      'Activity Type 120: Certificate Program',
      'Activity Type 141: Experiential Learning Activities',
      'Activity Type 161: Journal Club',
      'Activity Type 172: Live',
      'Activity Type 181: Posters',
      'Activity Type 231: Study Group',
      'Activity Type 741: Enduring Activity',
      'Annual Meeting',
      'Certifications',
      'Live Activity: 110 Case Presentation',
      'Live Activity: 130 Exhibits',
      'Live Activity: 150 Interactive Workshops',
      'Live Activity: 160 Journal Club',
      'Live Activity: 170 Lectures / Seminars',
      'Live Activity: 180 Posters',
      'Live Activity: 230 Study Groups',
      'Live Activity: 140 Experiential Skill Development',
      'Live Activity: 171 Live Webinars and Teleseminars',
      'Mixed Activity (172/741)',
      'Recorded pre-approved CPE Activity: 175 Lectures/Webinars',
      'Self Study Activity: 700 Audio-Based',
      'Self Study Activity: 710 Computer-Based (e.g., CD-ROM or non-internet based)',
      'Self Study Activity: 720 Printed',
      'Self Study Activity: 730 Video-Based',
      'Self Study Activity: 740 Web-Based'
    ]
  },

  // Optional fields
  instructor: {
    type: String,
    required: false,
    description: 'Name of the instructor (optional)'
  },
  cost: {
    type: Number,
    required: false,
    description: 'Cost of the course (required if costType is "paid")',
    min: 0
  },
  dateTime: {
    type: String,
    required: false,
    description: 'Date and time of the course (ISO 8601 format, required for scheduled courses)'
  },
  location: {
    type: String,
    required: false,
    description: 'Location of the course (e.g. city, venue, or full address)'
  },
  website: {
    type: String,
    required: false,
    description: 'URL to the course website'
  },
  description: {
    type: String,
    required: false,
    description: 'Additional description or details about the course'
  },

  // System fields
  status: {
    type: String,
    required: false,
    description: 'Course status: needs_review, info_missing, inactive, active',
    enum: ['needs_review', 'info_missing', 'inactive', 'active']
  },
  createdAt: {
    type: String,
    required: false,
    description: 'ISO 8601 timestamp of when the course was created'
  },
  updatedAt: {
    type: String,
    required: false,
    description: 'ISO 8601 timestamp of when the course was last updated'
  }
}

/** Course status values and display labels */
export const COURSE_STATUSES = [
  { value: 'needs_review', label: 'Needs Review' },
  { value: 'info_missing', label: 'Info Missing' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'active', label: 'Active' }
]

/**
 * Create a new course object with default values
 * @param {Object} data - Course data
 * @returns {Object} Course object
 */
export function createCourse(data = {}) {
  const now = new Date().toISOString()
  
  return {
    id: data.id || null,
    name: data.name || '',
    competencies: data.competencies || [],
    providerType: data.providerType || data.provider_type || '',
    providerName: data.providerName || data.provider_name || '',
    hours: data.hours !== undefined ? Number(data.hours) : null,
    credits: data.credits !== undefined ? Number(data.credits) : null,
    costType: data.costType || data.cost_type || 'free',
    courseType: data.courseType || data.course_type || '',
    isAccredited: data.isAccredited !== undefined ? data.isAccredited : (data.is_accredited !== undefined ? data.is_accredited : false),
    activityCode: data.activityCode || data.activity_code || null,
    expirationDate: data.expirationDate || data.expiration_date || null,
    instructor: data.instructor || null,
    cost: data.cost !== undefined ? Number(data.cost) : null,
    dateTime: data.dateTime || data.date_time || null,
    location: data.location || null,
    website: data.website || null,
    description: data.description || null,
    status: data.status || 'needs_review',
    createdAt: data.createdAt || data.created_at || now,
    updatedAt: data.updatedAt || data.updated_at || now
  }
}

/**
 * Convert course object from Rails API format (snake_case) to frontend format (camelCase)
 * @param {Object} railsCourse - Course object from Rails API
 * @returns {Object} Course object in frontend format
 */
export function fromRailsFormat(railsCourse) {
  if (!railsCourse) return null
  
  // Handle provider object if present (new format with provider association)
  const providerType = railsCourse.provider?.provider_type || railsCourse.provider_type
  const providerName = railsCourse.provider?.name || railsCourse.provider_name
  const providerId = railsCourse.provider?.id || railsCourse.provider_id
  
  return {
    id: railsCourse.id,
    name: railsCourse.name,
    competencies: (railsCourse.competencies || []).map(c => ({ id: c.id, position: c.position, name: c.name })),
    providerType: providerType,
    providerName: providerName,
    providerId: providerId,
    provider: railsCourse.provider || null,
    hours: railsCourse.hours ? Number(railsCourse.hours) : null,
    credits: railsCourse.credits ? Number(railsCourse.credits) : null,
    costType: railsCourse.cost_type,
    courseType: railsCourse.course_type,
    isAccredited: railsCourse.is_accredited || false,
    activityCode: railsCourse.activity_code,
    expirationDate: railsCourse.expiration_date,
    instructor: railsCourse.instructor,
    cost: railsCourse.cost ? Number(railsCourse.cost) : null,
    dateTime: railsCourse.date_time,
    location: railsCourse.location,
    website: railsCourse.website,
    description: railsCourse.description,
    status: railsCourse.status || 'needs_review',
    createdAt: railsCourse.created_at,
    updatedAt: railsCourse.updated_at,
    // Review metadata
    reviewCount: railsCourse.review_count !== undefined ? Number(railsCourse.review_count) : 0,
    averageOverallValue: railsCourse.average_overall_value !== undefined ? Number(railsCourse.average_overall_value) : null,
    averageEducationalContent: railsCourse.average_educational_content !== undefined ? Number(railsCourse.average_educational_content) : null,
    averageEngagement: railsCourse.average_engagement !== undefined ? Number(railsCourse.average_engagement) : null,
    lastReviewDate: railsCourse.last_review_date || null,
    // Performance indicators (for edit form)
    performanceIndicators: (railsCourse.performance_indicators || []).map(pi => ({
      id: pi.id,
      code: pi.code,
      description: pi.description,
      forRdn: pi.for_rdn,
      forNdtr: pi.for_ndtr
    }))
  }
}

/**
 * Convert course object from frontend format (camelCase) to Rails API format (snake_case)
 * @param {Object} course - Course object in frontend format
 * @returns {Object} Course object in Rails API format
 */
export function toRailsFormat(course) {
  if (!course) return null
  
  const railsData = {
    id: course.id,
    name: course.name,
    hours: course.hours,
    credits: course.credits,
    cost_type: course.costType,
    course_type: course.courseType,
    is_accredited: course.isAccredited !== undefined ? course.isAccredited : false,
    activity_code: course.activityCode || null,
    expiration_date: course.expirationDate || null,
    instructor: course.instructor || null,
    cost: course.cost || null,
    date_time: course.dateTime || null,
    location: course.location || null,
    website: course.website || null,
    description: course.description || null,
    status: course.status || null
  }
  
  // Use provider_id if available (new format), otherwise fall back to provider_type/provider_name
  if (course.provider_id || course.providerId) {
    railsData.provider_id = course.provider_id || course.providerId
  } else {
    railsData.provider_type = course.providerType
    railsData.provider_name = course.providerName
  }

  // Performance indicator IDs for course tagging (has_many through)
  if (course.performanceIndicatorIds && Array.isArray(course.performanceIndicatorIds)) {
    railsData.performance_indicator_ids = course.performanceIndicatorIds.filter(Boolean)
  }

  return railsData
}

/**
 * Validate a course object against the schema
 * @param {Object} course - Course object to validate
 * @returns {Object} Validation result with isValid and errors
 */
export function validateCourse(course) {
  const errors = []

  console.log('course', course)

  // Required fields
  if (!course.name || course.name.trim() === '') {
    errors.push('Course name is required')
  }

  if (!course.providerType) {
    errors.push('Provider type is required')
  }
  if (!course.providerName || course.providerName.trim() === '') {
    errors.push('Provider name is required')
  }
  if (course.credits === null || course.credits === undefined || course.credits <= 0) {
    errors.push('Credits must be a positive number')
  }
  if (!course.costType || !['free', 'paid'].includes(course.costType)) {
    errors.push('Cost type must be "free" or "paid"')
  }
  if (!course.courseType || !CourseSchema.courseType.enum.includes(course.courseType)) {
    errors.push('Course type is required')
  }

  // Conditional validations
  if (course.costType === 'paid' && (course.cost === null || course.cost === undefined || course.cost <= 0)) {
    errors.push('Cost is required for paid courses')
  }

  return {
    isValid: errors.length === 0,
    errors
  }
}

/**
 * Get all available provider types
 * @returns {Array<string>} Array of provider types
 */
export function getProviderTypes() {
  return CourseSchema.providerType.enum
}

/**
 * Get all available course types
 * @returns {Array<string>} Array of course types
 */
export function getCourseTypes() {
  return CourseSchema.courseType.enum
}

/**
 * Format course type for display (values are already display-friendly)
 * @param {string} courseType - Course type value
 * @returns {string} Formatted course type
 */
export function formatCourseType(courseType) {
  return courseType || ''
}

/**
 * Format course type short for display (same as full; values are already readable)
 * @param {string} courseType - Course type value
 * @returns {string} Short formatted course type
 */
export function formatCourseTypeShort(courseType) {
  return courseType || ''
}

/**
 * Get searchable fields for a course
 * @returns {Array<string>} Array of field names that should be searchable
 */
export function getSearchableFields() {
  return [
    'name',
    'description',
    'providerName',
    'instructor',
    'location'
  ]
}

